import React from "react";

import { Error } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";

export const StartEdappMigrationForm: React.FC = () => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { edappMigrationFormState, setEdappMigrationFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.warning.main}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.warning.main}
          data-testid="migrate-edapp-info-panel"
        >
          <Grid container alignItems="center">
            <Grid item xs={2} container justifyContent="center">
              <Error fontSize="large" />
            </Grid>
            <Grid item xs={10} style={{ paddingRight: "20px" }}>
              <ol>
                <li>
                  <Typography>
                    You are about to start an EdApp Migration to SafetyCulture.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    This process will migrate customers from EdApp to
                    SafetyCulture and may take some time depending on the size
                    of the organisation.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <b>Important:</b> Any validation should be done prior to the
                    migration. The validation can be done in{" "}
                    <b>admin.edapp.com Migration Tool</b>.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <b>Application ID</b> must be provided to identify the EdApp
                    to migrate.
                  </Typography>
                </li>
              </ol>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required disabled>
            Organisation ID
          </FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required>Application ID</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Application ID"
            placeholder="Enter EdApp Application ID"
            inputProps={{
              "data-testid": "input-application-id",
            }}
            error={edappMigrationFormState.applicationId === ""}
            fullWidth
            margin="normal"
            variant="outlined"
            value={edappMigrationFormState.applicationId}
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                applicationId: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={9}>
          <FormLabel required disabled>
            Region
          </FormLabel>
        </Grid>

        <Grid item xs={3}>
          <TextField
            name="Region"
            placeholder="AU, US, EU"
            inputProps={{
              "data-testid": "input-region",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                region: e.target.value,
              })
            }
            value={edappMigrationFormState.region}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Skip User Groups Migration</FormLabel>
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={edappMigrationFormState.skipUserGroupsMigration}
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                skipUserGroupsMigration: e.target.checked,
              })
            }
            data-testid="input-skip-user-groups-migration"
            inputProps={{ "aria-label": "Skip user groups migration" }}
          />
        </Grid>
      </Grid>

      {!edappMigrationFormState.skipUserGroupsMigration && (
        <Grid container direction="row" alignItems="center">
          <Grid item xs={5}>
            <FormLabel>Create Groups As</FormLabel>
          </Grid>

          <Grid item xs={7}>
            <FormControl fullWidth margin="normal">
              <Select
                value={edappMigrationFormState.createGroupsAs}
                onChange={(e) =>
                  setEdappMigrationFormState({
                    ...edappMigrationFormState,
                    createGroupsAs: e.target.value as "sites" | "groups",
                  })
                }
                inputProps={{
                  "data-testid": "input-create-groups-as",
                }}
              >
                <MenuItem value="sites">Sites</MenuItem>
                <MenuItem value="groups">Groups</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Organization Owner Email</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organization Owner Email"
            type="email"
            placeholder="Enter organization owner email (optional)"
            inputProps={{
              "data-testid": "input-organization-owner-email",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={edappMigrationFormState.organizationOwnerEmail}
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                organizationOwnerEmail: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Force Password Reset</FormLabel>
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={edappMigrationFormState.forcePassword}
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                forcePassword: e.target.checked,
              })
            }
            data-testid="input-force-password"
            inputProps={{ "aria-label": "Force password reset" }}
          />
        </Grid>
      </Grid>

      {edappMigrationFormState.forcePassword && (
        <Grid container direction="row" alignItems="center">
          <Grid item xs={5}>
            <FormLabel>Temporary Password</FormLabel>
          </Grid>

          <Grid item xs={7}>
            <TextField
              name="Temporary Password"
              type="password"
              placeholder="Enter temporary password"
              inputProps={{
                "data-testid": "input-password",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={edappMigrationFormState.password}
              onChange={(e) =>
                setEdappMigrationFormState({
                  ...edappMigrationFormState,
                  password: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      )}

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>
            EdApp roles override SafetyCulture seat permissions
          </FormLabel>
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={
              edappMigrationFormState.edAppRolesOverrideScSeatPermissions
            }
            onChange={(e) =>
              setEdappMigrationFormState({
                ...edappMigrationFormState,
                edAppRolesOverrideScSeatPermissions: e.target.checked,
              })
            }
            data-testid="input-edapp-roles-override-sc-seat-permissions"
            inputProps={{
              "aria-label":
                "EdApp roles override SafetyCulture seat permissions",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            When enabled, users matched during migration keep their EdApp role
            instead of receiving the SafetyCulture seat permission template. Use
            this only when EdApp roles are the source of truth for permissions.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};
